import { useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { UseBaseTable } from 'hooks/useBaseTable';
import useCustomExcelFileName from 'hooks/useCustomExcelFileName';
import { useWidgetPillsAndFilters } from 'hooks/useWidgetPillsAndFilters';

import { useMutationExportCsvReport } from 'api/mutations/exportCsvReport.mutation';
import { useQueryLargestDurationVarianceTableData } from 'api/queries/largestDurationVarianceTableData.query';

import { TKeys } from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { ITableLocalFiltersFields } from 'components/widgets/ChangeAnalysisTrackedActivities/tableLocalFilters/tableLocalFilters';
import { statusOptions } from 'components/widgets/KeyActivities/components/filter.utils';

export type UseLargestDurationVarianceProvider = ReturnType<typeof useLargestDurationVarianceProvider>;

interface Props {
    componentKey: string;
    widgetId?: string;
    globalFilterData: any;
    localFilters: any;
    compareVersionId: number | undefined;
    latestVersionId: number | undefined;
    title: string;
    projectId: number | null | undefined;
    contractId: number | null | undefined;
    table: UseBaseTable['table'];
    widgetViewMode: WidgetViewMode;
}

export const useLargestDurationVarianceProvider = ({
    widgetId,
    globalFilterData,
    localFilters,
    compareVersionId,
    latestVersionId,
    title,
    projectId,
    contractId,
    widgetViewMode,
}: Props) => {
    const isInPinboard = Boolean(widgetId);

    const [activeTab, setActiveTab] = useState<TKeys>(
        (WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ||
            WidgetViewModeUtils.isReportOrPinboardView(widgetViewMode)) &&
            localFilters?.activeTab
            ? (localFilters?.activeTab as TKeys)
            : 'next',
    );

    const form = useForm<ITableLocalFiltersFields>({
        defaultValues: {
            status: statusOptions,
            criticalPath: false,
        },
    });

    const formValues = useWatch({ control: form.control });

    const source = isInPinboard
        ? { ...localFilters, activeTab }
        : {
              ...globalFilterData,
              statusFilter: formValues?.status,
              isCriticalPath: formValues.criticalPath,
              activeTab,
          };

    const { filters, pills } = useWidgetPillsAndFilters({ widgetId, source, latestVersionId });

    const getLargestDurationVarianceTableDataPayload = {
        projectId: projectId,
        contractId: contractId,
        latestVersionId: latestVersionId,
        compareVersionId: compareVersionId,
        filter: {
            filters,
        },
    };

    const getLargestDurationVarianceTableData = useQueryLargestDurationVarianceTableData(
        getLargestDurationVarianceTableDataPayload,
    );

    const reportType = getLargestDurationVarianceTableData.data?.typeId || '';
    const fileName = useCustomExcelFileName({
        projectId,
        contractId,
        latestVersionId,
        title,
        prefix: 'Change_Analysis_',
    });

    return {
        ui: {
            state: {
                statusFilter: source.statusFilter,
                isCriticalPath: source.isCriticalPath,
                activityCodes: source.activityCodes,
                milestoneFragnet: source.milestoneFragnet,
                tags: source.tags,
                wbs: source.wbs,
                activeTab: source.activeTab,
            },
            setters: {
                setActiveTab,
            },
            isInPinboard,
            form,
            pills,
        },
        api: {
            getLargestDurationVarianceTableData: {
                instance: getLargestDurationVarianceTableData,
                payload: getLargestDurationVarianceTableDataPayload,
            },
            exportCsvReport: {
                instance: useMutationExportCsvReport(),
                payload: {
                    compareVersionId,
                    contractId,
                    filter: { filters },
                    latestVersionId,
                    period: activeTab,
                    projectId,
                    reportType,
                    fileName,
                },
            },
        },
    };
};
